import { resourceable, loadable, pagenable, actionable } from '@/store/mixins'

import { TASK_TYPES } from '@/utils/tasks'
import { formatForServer } from '@/utils/date'
import { intersection, symmetricalDifference } from '@/utils/immutable'
import { fromError } from '@/utils/common'

import { types, isDefectByPoint, isDefectCommon } from '@/models/tasks'
import { planTypes } from '@/models/plans'
import {download, getContentDispositionFileNameFromAxiosResponse} from '@/utils/browser'

export default {
  namespaced: true,

  mixins: [
    resourceable({
      name: 'task',
      initial: null,
      from: async ({ api, commit, payload: { id, withUsers, withLastMessage, withMessagesCount, withTaskRejectionsExistAsTaskReasonDefect, withTaskRejectionsExistThroughAstWorks } }) => {
        const task = await api.tasks.show(id, {
          params: {
            include: [
              withUsers && 'creator',
              withUsers && 'acceptanceOfWorkContractor.organization',
              withUsers && 'defectGeneralContractor.organization',
              withUsers && 'memberWorkers.user.organization'
            ].filter(is),
            append: [
              withLastMessage && 'last_message_with_creator',
              withMessagesCount && 'messages_count_all',
              withTaskRejectionsExistAsTaskReasonDefect && 'has_belongs_to_task_rejection_with_type_reject_acceptance_by_defect_with_ast_work_as_task_reason_defect',
              withTaskRejectionsExistThroughAstWorks && 'has_belongs_to_task_rejection_with_type_reject_acceptance_by_defect_with_ast_work_through_ast_works'
            ].filter(is)
          }
        }).then(x => x?.data?.data || {})

        commit('REFRESH_TASK', task)

        return task
      }
    }),

    pagenable({
      name: 'tasks',
      from: ({ api, getters, page, payload: { 
        type, 
        from, 
        to,

        all = false, 

        filter = {},
        size = 60,
        sort,

        polygons,

        withUnread
      } }) => api.tasks.index({
        params: {
          filter: {
            ...filter,

            'type': type,
            'project_id': getters['project/project'].id,

            ...from && { 'created_from': formatForServer(from) },
            ...to && { 'created_to': formatForServer(to) },
            ...polygons && { 'polygon_ids': polygons.map(({ id }) => id) }
          },
          include: [
            'workerOrganization', 'creator', 'project', 'members.user.organization', 'memberInitiator.user.organization', 'memberWorkers.user.organization',
            'memberInspectors.user.organization', 'acceptanceOfWorkContractor.organization', 'defectGeneralContractor.organization',

            ...type === TASK_TYPES.DEFECTS_AND_VIOLATIONS ? [
              'defectGeneralContractor.organization',
              'objectsBuildingOrderConnection.object',
              'objectsAcceptanceOfWorkConnection.object',
              'linkedFirstDefectProjectObject'
            ] : [],

            ...type === TASK_TYPES.BUILDING_ORDER ? [
              'subjectsBuildingOrderConnection.subject'
            ] : [],

            ...type === TASK_TYPES.ACCEPTANCE_OF_WORK ? [
              'subjectsAcceptanceOfWorkConnection.subject',
              'acceptanceOfWorkContractor.organization'
            ] : []
          ],
          page: {
            number: page,
            size: all ? 0 : size
          },
          append: [
            withUnread && 'unread_changes_exists',
            type === TASK_TYPES.ACCEPTANCE_OF_WORK && 'has_belongs_to_task_rejection_with_type_reject_acceptance_by_defect_with_ast_work_through_ast_works',
            type === TASK_TYPES.ACCEPTANCE_OF_WORK && 'last_message_with_creator',
            type === TASK_TYPES.ACCEPTANCE_OF_WORK && 'messages_count_all',
            type === TASK_TYPES.ACCEPTANCE_OF_WORK && 'ast_acts_ks2',
            type === TASK_TYPES.ACCEPTANCE_OF_WORK && 'ast_acts_ks2_errors_exists',
            type === TASK_TYPES.DEFECTS_AND_VIOLATIONS && 'has_belongs_to_task_rejection_with_type_reject_acceptance_by_defect_with_ast_work_as_task_reason_defect'
          ].filter(is),
          ...sort && { sort }
        }
      }).then(x => x?.data?.data || [])
    }),

    actionable({
      name: 'fetchTasksSliced',
      at: ({ api }, { projectId, type, from, to, filterByDateType, sort, withMembers, withProject, withMainDocument, withKs2, withKs2ErrorsExist }) => {
        const dateKeys = ({
          created: { from: 'created_from', to: 'created_to' },
          finished: { from: 'finished_from', to: 'finished_to' },
          approved: { from: 'approved_from', to: 'approved_to' }
        })[filterByDateType || 'created']

        const payload = {
          params: {
            filter: {
              type,
              ...from && { [dateKeys.from]: formatForServer(from, { iso: true }) } || {},
              ...to && { [dateKeys.to]: formatForServer(to, { iso: true }) } || {}
            },
            include: [
              withProject && 'project',
              withMembers && 'members',
              withMainDocument && 'main_document'
            ].filter(is),
            append: [
              withKs2 && 'ast_acts_ks2',
              withKs2ErrorsExist && 'ast_acts_ks2_errors_exists'
            ].filter(is),
            ...sort && { sort }
          }
        }

        return api.tasks.getTasksSliced({ projectId, payload })
      }
    }),

    resourceable({
      name: 'tasksByPolygons',
      from: ({ api }, { type, polygons }) => api.tasks.index({
        params: {
          filter: {
            type,
            ...polygons && { polygon_ids: polygons.map(({ id }) => id) }
          },
          include: [
            'workerOrganization', 'creator', 'project', 'memberInitiator.user.organization', 'memberWorkers.user.organization',
            'memberInspectors.user.organization', 'acceptanceOfWorkContractor.organization', 'defectGeneralContractor.organization',

            ...type === TASK_TYPES.DEFECTS_AND_VIOLATIONS ? [
              'defectGeneralContractor.organization',
              'objectsBuildingOrderConnection.object',
              'objectsAcceptanceOfWorkConnection.object'
            ] : [],

            ...type === TASK_TYPES.BUILDING_ORDER ? [
              'subjectsBuildingOrderConnection.subject',
              'members.user.organization'
            ] : [],

            ...type === TASK_TYPES.ACCEPTANCE_OF_WORK ? [
              'subjectsAcceptanceOfWorkConnection.subject',
              'acceptanceOfWorkContractor.organization'
            ] : []
          ]
        }
      }).then(x => x?.data?.data || [])
    }),

    resourceable({
      name: 'tasksCount',
      from: ({ api, getters, payload: { 
        type,
        contractorOrganization,
        byStatus: by_status, 
        byState: by_state,
        byKind: by_kind_defect
      } }) => api.tasksV2.getTasksCount({
        filter: {
          type,
          project_id: getters['project/project'].id,
          ...contractorOrganization && { contractor_organization_id: contractorOrganization.id }
        },
        append: Object.entries({
          by_status,
          by_state,
          by_kind_defect
        }).filter(([_, v]) => v).map(([k, _]) => k)
      }).then(x => x?.data?.data || {})
    }),

    resourceable({
      name: 'tasksCountByOrganization',
      from: ({ api, getters, payload: {
        type
      } }) => api.tasksV2.getTasksCountByOrganization({ 
        filter: {
          type,
          project_id: getters['project/project'].id 
        }
      }).then(x => x?.data?.data || [])
    }),

    resourceable({
      name: 'tasksCountByUsers',
      from: ({ api, getters }, {
        type,
        from,
        to,
        byStatus: by_status, 
        byState: by_state,
        byKind: by_kind_defect,
        byTransition: by_transition
      }) => api.tasksV2.getTasksCountByUsers({
        filter: {
          project_id: getters['project/project'].id,
          ...from && { updated_from: from },
          ...to && { updated_to: to },
          type,
          only_engineer: 1
        },
        append: [
          Object.entries({
            by_status,
            by_state,
            by_kind_defect,
            by_transition
          }).filter(([_, v]) => v).map(([k, _]) => k) 
        ]
      }).then(x => x?.data?.data || [])
    }),

    resourceable({
      name: 'tasksCountInTime',
      from: ({ api, getters, payload: {
        type,
        by
      } }) => api.tasksV2.getTasksCountInTime({
        filter: {
          type,
          by,
          project_id: getters['project/project'].id
        }
      }).then(x => x?.data?.data || [])
    }),

    resourceable({
      name: 'workingTask',
      initial: null,
      from: async ({ api, dispatch, payload: {
        id
      } }) => {
        const task = await api.tasks.show(id, {
          params: {
            include: [
              'creator',
              'acceptanceOfWorkContractor.organization',
              'defectGeneralContractor.organization',
              'memberWorkers.user.organization',

              'subjectsAcceptanceOfWorkConnection.subject',
              'subjectsBuildingOrderConnection.subject'
            ]
          }
        }).then(x => x?.data?.data || {})

        await dispatch('fetchRelatedTasks', task)

        return task
      }
    }),

    resourceable({
      name: 'lotDefects',
      once: true,
      from: ({ api, payload: { search } }) => api.dirsV2.getLotDefects({
        params: {
          filter: {
            name: search ? search : null
          },
          page: {
            size: 0
          }
        }
      }).then(x => x.data?.data || []) 
    }),


    resourceable({
      name: 'defectKinds',
      from: ({ api }) => api.tasksV2.getDefectKinds().then(x => x.data?.data || [])
    }),

    resourceable({
      name: 'editableFieldsWithValidation',
      from: ({ api }, { id }) => 
        api.tasks.getTaskEditableFieldsWithValidation({ id })
          .then(x => x.data?.data || [])
    }),

    resourceable({
      name: 'creatableFields',
      resetBeforeFetch: true,
      from: ({ api, getters }, { type, subtype, contractor_id } = {}) => 
        api.tasks.getProjectTasksCreatableFields({
          project: getters['project/project'],
          type,
          subtype,
          ...contractor_id && { data: { contractor_id } }
        })
          .then(x => x.data?.data || [])
    }),

    resourceable({
      name: 'allowedActions',
      from: ({ api }, { task }) =>
        api.tasksV2.getAllowedActions({ task })
          .then(r => r.map(x => ({
            ...x,
            warnings: [
              x.reason_disable
            ].filter(is),
            messages: x.messages || []
          })))
    }),

    resourceable({
      name: 'taskMessages',
      from: ({ api }, { task, withUser }) => 
        api.tasksV2.getMessages({ task, withUser }).then(r => r.data.data || [])
    }),

    resourceable({
      name: 'taskAnalysis',
      from: ({ api }, { taskId, withProjectDocuments, withPromise }) => 
        api.tasks.getAnalysis({ taskId, payload: {
          params: {
            include: [
              withProjectDocuments && 'project_documents',
              withPromise && 'promise'
            ].filter(is)
          }
        } }).then(r => r.data.data || [])
    }),

    resourceable({
      name: 'taskAnalysisEvent',
      from: ({ api }, { taskId, eventId, withProjectDocuments, withPromise }) => 
        api.tasks.getAnalysisById({ taskId, eventId, payload: {
          params: {
            include: [
              withProjectDocuments && 'project_documents',
              withPromise && 'promise'
            ].filter(is)
          }
        } }).then(r => r.data.data)
    }),

    actionable({
      name: 'fetchTaskAnalysisResult',
      loadable: true,
      at: ({ api }, { taskId, eventId }) => 
        api.tasks.getAnalysisResult({ taskId, eventId }).then(r => r.data)
    }),

    resourceable({
      name: 'taskTemplates',
      from: ({ api }, { task }) =>
        api.tasksV2.getTemplates({ task }).then(r => r.data.data || [])
    }),

    actionable({
      name: 'downloadTaskTemplate',
      loadable: true,
      at: ({ api }, { task, taskTemplateId }) =>
        api.tasksV2.renderTemplate({ task, taskTemplateId })
          .then(r => {
            const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }))
            const name = getContentDispositionFileNameFromAxiosResponse(r)

            download(url, { openable: true, name })
          })
    }),

    actionable({
      name: 'setTaskMessages',
      at: ({ state }, messages) =>
        state.taskMessages = messages
    }),

    actionable({
      name: 'addDefectMark',
      at: ({ api }, { task, photoId, yolo }) => api.tasksV2.addDefectMark({ task, photoId, yolo }) 
    }),

    actionable({
      name: 'addStreetFalconDefectMark',
      at: ({ api }, { task, photoId, yolo }) => api.tasksV2.addStreetFalconDefectMark({ task, photoId, yolo }) 
    }),

    actionable({
      name: 'applyPhotoAnnotation',
      loadable: true,
      at: ({ api }, { capture, createdShapes, updatedShapes, removedShapes }) => 
        api.tasks.applyPhotoAnnotation({ capture, createdShapes, updatedShapes, removedShapes })
          .then(r => {
            wantSomeMagic()
            return r
          })
    }),

    actionable({
      name: 'getPhotoAnnotation',
      loadable: true,
      at: async ({ api }, { capture, withContent }) => {
        const { annotation, can_edit } = await api.tasks.getPhotoAnnotation({ capture })

        const contentUrl = annotation?.image?.storage_url

        const content = withContent && contentUrl && await fetch(contentUrl).then(r => r.text())

        return {
          ...annotation,
          ...content && { content },
          can_edit
        }
      }
    }),

    actionable({
      name: 'storeFilter',
      at: ({ state }, { filter, filterForServer }) => {
        state.filter = filter
        state.filterForServer = filterForServer
      }
    }),

    actionable({
      name: 'storeTaskPolygons',
      loadable: true,
      at: ({ api }, { taskId, planId, planType, polygonsForCreate = [], polygonsForUpdate = [], polygonsForRemove = [] }) => api.tasks.update(taskId, {
        polygons: {
          object_id: planId,
          object_type: ({
            [planTypes.Common]: 'floor_plan',
            [planTypes.Work]: 'floor_work_plan_layer'
          })[planType],
          insert: polygonsForCreate,
          update: polygonsForUpdate,
          delete: polygonsForRemove.pluck('id')
        }
      }).catch(fromError)
    }),

    loadable({ action: 'getTasks' }),
    loadable({ action: 'showTask' }),
    loadable({ action: 'storeTaskWithError' }),
    loadable({ action: 'storeDefectImages' }),
    loadable({ action: 'updateTask' }),
    loadable({ action: 'updateTaskWithError' })
  ],

  state: {
    filter: {},
    filterForServer: {},

    requestedTask: null,
    relatedTasks: [],
    selectedTasks: [],

    taskMessages: [],

    editableFields: [],
    taskTypes: [],
    gprTasksCountData: [],
    filesCount: 0
  },

  getters: {
    filter: state => state.filter,
    filterForServer: state => state.filterForServer,

    requestedTask: state => state.requestedTask,
    relatedTasks: state => state.relatedTasks,
    selectedTasks: state => state.selectedTasks,

    taskMessages: state => state.taskMessages,
    editableFields: state => state.editableFields,

    editableFieldsErrorsByName: state => name => state.editableFieldsWithValidation.find(x => x.name === name)?.errors,

    creatableFieldsByName: state => name => state.creatableFields.find(x => x.name === name) || false,

    hasRequiredFields: state => [...state.creatableFields || [], ...state.editableFieldsWithValidation || []].some(x => x.required || x.is_require),
    hasEditableFields: state => state.editableFields?.length || state.editableFieldsWithValidation?.length,

    isCreatableField: state => name => (state.creatableFields || []).some(x => x.name === name),
    isCreatableFieldAny: state => (...names) => !!intersection((state.creatableFields || []).pluck('name'), names).length,
    isEditableField: state => name => state.editableFields.includes(name) || state.editableFieldsWithValidation.some(x => x.name === name),
    isEditableFieldAny: state => (...names) => !!intersection((state.editableFields), names).length || !!intersection((state.editableFieldsWithValidation.pluck('name')), names).length,

    isRequiredField: state => name => [
      ...state.creatableFields || [],
      ...state.editableFieldsWithValidation || []
    ].some(x => x.name === name && (x.required || x.is_require || false)),

    hasRequiredFieldValue: state => (k, v) => [
      ...state.creatableFields || [], 
      ...state.editableFieldsWithValidation || []
    ].some(x => x.name === k && (x.available_values || []).includes(v)),

    getRequiredFieldValues: state => k => [
      ...state.creatableFields || [], 
      ...state.editableFieldsWithValidation || []
    ].find(x => x.name === k)?.available_values || [],

    getRequiredFieldOptions: state => k => [
      ...state.creatableFields || [], 
      ...state.editableFieldsWithValidation || []
    ].find(x => x.name === k)?.options || {},

    taskTypesOptionsCreate: state => {
      return state.taskTypes.filter(type => type.value !==
        'plain');
    },

    isBuildingOrderType: state => state.task.type === TASK_TYPES.BUILDING_ORDER,
    isDefectsType: state => state.task.type === TASK_TYPES.DEFECTS_AND_VIOLATIONS,
    isCheckingActType: state => state.task.type === TASK_TYPES.CHECKING_ACT,
    isPhotoOrderType: state => state.task.type === TASK_TYPES.PHOTO_ORDER,
    isPlainType: state => state.task.type === TASK_TYPES.PLAIN,
    isAcceptanceType: state => state.task.type === TASK_TYPES.ACCEPTANCE_OF_WORK,
    isApprovalType: state => state.task.type === TASK_TYPES.APPROVAL_PROCESS,
    contractorTasksCount: state => contractorId => {
      const tasksCount = state.gprTasksCountData.find(item => item.contractorId === contractorId)
      return tasksCount ? tasksCount.data : null
    },
    contractorId: state => state.task?.data?.contractor_id || null,
    objects: state => state.task?.data?.objects || [],
    withoutDefectPhotos: state => state.task?.data?.without_defect_photos || [],
    checklistPhotos: state => state.task?.data?.checklist_photos || [],

    taskPhotosCount: state => {
      const withoutDefectPhotosCount = state.task.data?.without_defect_photos?.length || 0
      const checklistPhotosCount = state.task.data?.checklist_photos?.length || 0
      const defectPhotosCount = state.relatedTasks?.length || 0

      return withoutDefectPhotosCount + checklistPhotosCount + defectPhotosCount
    },

    filesCount: state => state.filesCount
  },

  mutations: {
    REFRESH_TASK: (state, x) => {
      state.tasks = state.tasks.map(y => y.id === x.id ? x : y)
    },

    REQUEST_TASK: (state, { id }) => state.requestedTask = { id },

    SET_RELATED_TASKS: (state, tasks) => state.relatedTasks = tasks,
    CLEAR_RELATED_TASKS: state => state.relatedTasks = [],
    REMOVE_RELATED_TASK: (state, task) => state.relatedTasks = state.relatedTasks.filter(x => x.id !== task.id),

    TOGGLE_TASKS: (state, tasks) => state.selectedTasks = symmetricalDifference([...state.selectedTasks], tasks, (x, y) => x.id === y.id),
    CLEAR_SELECTED_TASKS: state => state.selectedTasks = [],

    CLEAR_TASKS: state => {
      state.tasks = []
      state.selectedTasks = []
    },

    // NOT REVIEWED

    SET_SELECTED_TASK: (state, payload) => {
      state.task = payload;
    },
    SET_EDITABLE_FIELDS: (state, payload) => {
      state.editableFields = payload;
    },
    SET_ALLOWED_ACTIONS: (state, payload) => {
      state.allowedActions = payload;
    },
    SET_TASK_TYPES: (state, payload) => {
      state.taskTypes = payload;
    },
    SET_GPR_TASKS_COUNT_DATA: (state, payload) => {
      state.gprTasksCountData.push(payload)
    },
    CLEAR_GPR_TASKS_COUNT_DATA: state => state.gprTasksCountData = [],
    SET_FILES_COUNT: (state, payload) => state.filesCount = payload,
    INCREASE_FILES_COUNT: state => state.filesCount++,
    DECREASE_FILES_COUNT: state => state.filesCount--
  },

  actions: {
    storeDefectImage: function (_, payload) {
      return this.$api.tasksV2.storeDefectImage(payload).then(x => x?.data?.data);
    },

    storeDefectImages: function(_, { task, images, forDefectCommon, forDefectByPoint }) {
      let r

      r ||= !images.length && Promise.resolve([])

      r ||= (forDefectCommon || isDefectCommon(task)) && this.$api.tasksV2.storeImages({ type: 'defect_and_violation_photo_defect', images })
        .then(x => x.data.data || [])
        .then(x => x.map(({ id }) => id))

      r ||= (forDefectByPoint || isDefectByPoint(task)) && this.$api.tasksV2.storeDefectImage({ image: images[0] })
        .then(x => x.data.data || {})
        .then(({ id }) => [id])

      r ||= Promise.resolve([])

      return r.catch(fromError)
    },

    toggleTasks({ commit }, tasks) {
      commit('TOGGLE_TASKS', tasks)
    },

    clearSelectedTasks({ commit }) {
      commit('CLEAR_SELECTED_TASKS')
    },

    async fetchRelatedTasks({ commit, dispatch }, task) {
      const relations = [...task.subjects_acceptance_of_work_connection || [], ...task.subjects_building_order_connection || []]

      if (relations.length) {
        const ids = relations.map(({ task_subject_id }) => task_subject_id)

        const tasks = await dispatch('getTasks', {
          params: {
            filter: {
              id: ids
            },
            include: [
              'creator.organization',
              'linkedDefectProjectObjects'
            ],
            page: {
              size: 0
            }
          }
        })

        commit('SET_RELATED_TASKS', tasks.data)
      } else {
        commit('CLEAR_RELATED_TASKS')
      } 
    },

    removeRelatedTask({ commit }, task) {
      commit('REMOVE_RELATED_TASK', task)
    },

    clearTasks({ commit }) {
      commit('CLEAR_TASKS')
    },

    selectTask({ commit }, task) {
      commit('SET_SELECTED_TASK', task)
    },

    deselectTask({ commit }) {
      commit('SET_SELECTED_TASK', null)
      commit('SET_FILES_COUNT', 0)
    },

    // NOT REVIEWED

    clearEditableFields: async function ({commit}) {
      await commit('SET_EDITABLE_FIELDS', []);
    },
    clearAllowedActions: async function ({commit}) {
      await commit('SET_ALLOWED_ACTIONS', [])
    },
    clearTask: async function ({commit}) {
      commit('SET_SELECTED_TASK', null)
      commit('SET_FILES_COUNT', 0)
    },
    getTasks: async function (_, payload = null) {
      let {data} = await this.$api.tasks.index(payload);
      return data;
    },
    storeTask: async function (_, { payload }) {
      const { data } = await this.$api.tasks.store(payload);

      wantSomeMagic()
      
      return data;
    },

    storeTaskWithError({ dispatch }, { payload }) {
      return dispatch('storeTask', { payload })
        .catch(fromError)
    },

    storeDefectAndViolationTask: async function ({ dispatch }, {taskData}) {
      return dispatch('storeTask', {
        payload: {
          type: 'defects_and_violations',
          data: taskData
        }
      }).then(r => r.data);
    },

    storeDefectAndViolationTaskWithError: function({ dispatch }, { taskData }) {
      return dispatch('storeDefectAndViolationTask', { taskData })
        .catch(fromError)
    },

    showTask: async function ({state, commit, dispatch}, {
      taskId,
      payload
    }) {
      commit('REQUEST_TASK', { id: taskId })

      // TODO@temp
      state.taskLoading = true

      const result = await this.$api.tasks.show(taskId, payload)

      const task = result.data.data

      commit('SET_SELECTED_TASK', task)
      commit('REFRESH_TASK', task)
      dispatch('account/refreshTask', task, { root: true })

      const { files_count, type } = task || {}

      files_count && commit('SET_FILES_COUNT', files_count)

      type !== TASK_TYPES.APPROVAL_PROCESS && await dispatch('fetchRelatedTasks', task)
      await dispatch('fetchEditableFieldsWithValidation', task)
      // type !== TASK_TYPES.APPROVAL_PROCESS && await dispatch('getTaskEditableFields', { taskId: taskId })
      await dispatch('fetchAllowedActions', { task })

      type === TASK_TYPES.APPROVAL_PROCESS && await dispatch('approval/fetchApproval', { processId: taskId }, { root: true })

      // TODO@temp
      state.taskLoading = false

      return result.data;
    },
    updateTask: async function ({dispatch}, {
      taskId,
      taskType,
      payload
    }) {
      let {data} = await this.$api.tasks.update(taskId, payload);

      await dispatch('showTask', {
        taskId: taskId,
        payload: {
          params: {
            include: [
              'project',
              'creator',
              'children',
              'members.user.organization',
              'memberInitiator.user.organization',
              'memberInspectors.user.organization',
              'memberWorkers.user.organization',
              'membersTrashed.user.organization',
              'defectGeneralContractor.organization',
              'acceptanceOfWorkContractor.organization',
              'objectsBuildingOrderConnection.object',
              'objectsAcceptanceOfWorkConnection.object',
              'subjectsBuildingOrderConnection.subject',
              'subjectsAcceptanceOfWorkConnection.subject',
              'linkedDefectProjectObjects',

              ...taskType === types.ACCEPTANCE_OF_WORK && [
                'astContract',
                'astWorks.taskRejections.taskReasonDefects'
              ] || [],

              ...taskType === types.DEFECTS_AND_VIOLATIONS && [
                'astContract',
                'taskRejections.taskReasonDefects',
                'taskRejections.astWorks.astContract'
              ] || []
            ],
            append: [
              'unseen_files_exists',
              'unseen_messages_exists',

              ...taskType === types.DEFECTS_AND_VIOLATIONS && [
                'has_belongs_to_task_rejection_with_type_reject_acceptance_by_defect_with_ast_work_as_task_reason_defect'
              ] || [],

              ...taskType === types.ACCEPTANCE_OF_WORK && [
                'has_belongs_to_task_rejection_with_type_reject_acceptance_by_defect_with_ast_work_through_ast_works',
                'last_message_with_creator',
                'messages_count_all',
                'ast_acts_ks2',
                'ast_acts_ks2_errors_exists'
              ] || []
            ]
          }
        }
      });

      wantSomeMagic()

      return data;
    },

    updateTaskWithError({ dispatch }, { taskId, taskType, payload }) {
      return dispatch('updateTask', { taskId, taskType, payload })
        .catch(fromError)
    },

    destroyTask: async function (_context, payload) {
      let {data} = await this.$api.tasks.destroy(payload);
      return data;
    },
    multipleDestroyTask: async function (_context, payload) {
      let {data} = await this.$api.tasks.multipleDestroy(payload);
      return data;
    },
    restoreTask: async function (_context, id) {
      let {data} = await this.$api.tasks.restore(id);
      return data;
    },
    startTask: async function (_context, id) {
      let {data} = await this.$api.tasks.startTask(id);
      return data;
    },
    sendToRevisionTask: async function (_context, id) {
      let {data} = await this.$api.tasks.sendToRevisionTask(id);
      return data;
    },
    finishTask: async function (_context, id) {
      let {data} = await this.$api.tasks.finishTask(id);
      return data;
    },
    completeTask: async function (_context, id) {
      let {data} = await this.$api.tasks.completeTask(id);
      return data;
    },
    getTaskTypes: async function ({commit}) {
      let {data} = await this.$api.tasks.getTaskTypes();
      commit('SET_TASK_TYPES', data.data);
      return data;
    },
    getMessages: async function (_context, payload) {
      let {data} = await this.$api.tasks.getMessages(payload);
      return data;
    },
    sendMessage: async function (_context, {
      taskId,
      payload
    }) {
      let {data} = await this.$api.tasks.sendMessage(taskId, payload);
      return data;
    },
    updateMessage: async function (_context, {
      taskId,
      messageId,
      payload
    }) {
      let {data} = await this.$api.tasks.updateMessage(taskId, messageId,
        payload);
      return data;
    },
    getTaskEditableFields: async function ({commit}, {taskId}) {
      let {data} = await this.$api.tasks.getTaskEditableFields(taskId);
      commit('SET_EDITABLE_FIELDS', data.data);
      return data;
    },
    makeTaskAction: async function ({ dispatch }, {
      taskId,
      taskType,
      payload
    }) {
      let { data } = await this.$api.tasks.makeTaskAction(taskId, payload);
      dispatch('showTask', {
        taskId: taskId,
        payload: {
          params: {
            include: [
              'project',
              'creator',
              'children',
              'members.user.organization',
              'memberInitiator.user.organization',
              'memberWorkers.user.organization',
              'memberInspectors.user.organization',
              'membersTrashed.user.organization',
              'defectGeneralContractor.organization',
              'acceptanceOfWorkContractor.organization',
              'objectsBuildingOrderConnection.object',
              'objectsAcceptanceOfWorkConnection.object',
              'subjectsBuildingOrderConnection.subject',
              'subjectsAcceptanceOfWorkConnection.subject',
              'linkedDefectProjectObjects',

              ...taskType === types.ACCEPTANCE_OF_WORK && [
                'astContract',
                'astWorks.taskRejections.taskReasonDefects'
              ] || [],

              ...taskType === types.DEFECTS_AND_VIOLATIONS && [
                'astContract',
                'taskRejections.taskReasonDefects',
                'taskRejections.astWorks.astContract'
              ] || []
            ],
            append: [
              ...taskType === types.DEFECTS_AND_VIOLATIONS && [
                'has_belongs_to_task_rejection_with_type_reject_acceptance_by_defect_with_ast_work_as_task_reason_defect'
              ] || [],

              ...taskType === types.ACCEPTANCE_OF_WORK && [
                'has_belongs_to_task_rejection_with_type_reject_acceptance_by_defect_with_ast_work_through_ast_works',
                'last_message_with_creator',
                'messages_count_all',
                'ast_acts_ks2',
                'ast_acts_ks2_errors_exists'
              ] || []
            ]
          }
        }
      });

      return data;
    },
    getTaskEvents: async function (_context, {
      taskId,
      payload
    }) {
      let {data} = await this.$api.tasks.getTaskEvents(taskId, payload);
      return data;
    },
    getTaskFiles: async function (_context, {
      taskId,
      payload
    }) {
      let {data} = await this.$api.tasks.getTaskFiles(taskId, payload);
      return data;
    },
    uploadTaskFiles: async function (_context, {
      taskId,
      payload
    }) {
      let {data} = await this.$api.tasks.uploadTaskFiles(taskId, payload);
      return data;
    },
    deleteTaskFiles: async function (_context, {
      taskId,
      payload
    }) {
      let {data} = await this.$api.tasks.deleteTaskFiles(taskId, payload);
      return data;
    },
    createTaskByImageDefects: async function (_context, {imageDefectIds}) {
      let {data} = await this.$api.tasks.createTaskByImageDefects(
        {image_defects: imageDefectIds});
      return data.data.id;
    },
    getTaskAvailableFieldsForCreate: async function (_context, payload) {
      let {data} = await this.$api.tasks.getTaskAvailableFieldsForCreate(
        payload);
      return data;
    },
    storeTaskImage: async function (_context, {taskId, payload}) {
      let {data} = await this.$api.tasksV2.storeTaskImage(taskId, payload);
      return data;
    },
    getTaskImage: async function (_context, {taskId, taskImageId}) {
      let {data} = await this.$api.tasksV2.getTaskImage(taskId, taskImageId);
      return data;
    },
    getTaskFillingFields: async function (_context, {taskId, docTemplateId}) {
      let {data} = await this.$api.tasksV2.getTaskFillingFields(taskId, docTemplateId);
      return data;
    },
    storeTaskFillingFields: async function (_context, {taskId, docTemplateId, payload}) {
      return await this.$api.tasksV2.storeTaskFillingFields(taskId, docTemplateId, payload);
    },
    deleteTaskImage: async function (_context, {taskId, taskImageId}) {
      let {data} = await this.$api.tasksV2.deleteTaskImage(taskId, taskImageId);
      return data;
    },
    getGprTasksCount: async function ({commit}, {projectId, payload, contractorId}) {
      const data = (await this.$api.tasksV2.getGprTasksCount(projectId, payload))?.data?.data || []
      commit('SET_GPR_TASKS_COUNT_DATA', {contractorId, data})
      return data
    },
    clearGprTasksCount: function ({commit}) {
      commit('CLEAR_GPR_TASKS_COUNT_DATA')
    },
    increaseFilesCount: ({ commit }) => commit('INCREASE_FILES_COUNT'),
    decreaseFilesCount: ({ commit }) => commit('DECREASE_FILES_COUNT')
  }
};

